import { formatMoney } from "@justhome/common";
import { OfferPreview } from "@justhome/api-core";
import clsx from "clsx";
import { FC } from "react";
import { formatInterestPeriodRange } from "../lib/formatting";
import { Locale } from "../lib/i18n";
import { offerLabels } from "../lib/labels";
import ConditionallyFeasableBadge from "../Offer/ConditionallyFeasableBadge";
import OfferBadges from "../Offer/OfferBadges";
import SustainableBadge from "../Offer/SustainableBadge";
import Skeleton from "../Skeleton";

interface OfferBodyProps {
  offer?: OfferPreview;
  isLowestMonthlyInstallment?: boolean;
  isLowestInterestRate?: boolean;
  locale: Locale;
  addBadgePlaceholder?: boolean;
  isLoading?: boolean;
}

const OfferBody: FC<OfferBodyProps> = ({
  offer,
  isLowestMonthlyInstallment,
  isLowestInterestRate,
  locale,
  addBadgePlaceholder,
  isLoading,
}) => {
  const loans = offer?.loans;
  const monthlyInstallment = offer?.monthlyInstallment;
  const sustainable = offer?.sustainable;
  const conditionallyFeasible = offer?.conditionallyFeasible;
  const labels = offerLabels[locale];
  const loan = loans && loans[0];
  const productProviderLogo = loan?.loanProvider?.logoFilename;
  const productProviderName = loan?.loanProvider?.name;

  /* eslint-disable @next/next/no-img-element */
  return (
    <div className="flex flex-col pb-3xs items-center">
      {!isLoading && (
        <OfferBadges
          addPlaceholder={addBadgePlaceholder}
          isLowestInterestRate={isLowestInterestRate}
          isLowestMonthlyInstallment={isLowestMonthlyInstallment}
          locale={locale}
        />
      )}
      <div className="flex w-full justify-center items-center mb-xs min-h-m">
        <Skeleton
          isLoading={isLoading}
          width={24}
          height={24}
          className="mr-2xs"
        >
          {productProviderLogo && (
            <img
              className="h-3s w-3s object-contain mr-2xs s:w-2m"
              alt=""
              src={productProviderLogo}
            />
          )}
        </Skeleton>
        <Skeleton isLoading={isLoading} width={160} height={24}>
          <h3
            title={productProviderName}
            className="overflow-hidden text-ellipsis whitespace-nowrap text-app-body-1"
          >
            {productProviderName}
          </h3>
          {sustainable && <SustainableBadge locale={locale} />}
          {conditionallyFeasible && (
            <ConditionallyFeasableBadge locale={locale} />
          )}
        </Skeleton>
      </div>
      <Skeleton isLoading={isLoading} width="80%" height={50}>
        <span className="text-center">
          <span
            className={clsx("text-app-h2 rounded-xs px-3xs font-book", {
              "bg-purple-50 text-purple-725": isLowestMonthlyInstallment,
            })}
          >
            {formatMoney(monthlyInstallment || 0, false, "de")}
          </span>
          <br />
          <span className="text-app-body-2 text-neutral-675/50">
            {labels.monthly}{" "}
          </span>
          <span className="text-app-body-2 text-neutral-675/50">
            {labels.with}
          </span>{" "}
          <span className="text-app-body-2 whitespace-nowrap">
            {offer && formatInterestPeriodRange(offer, locale)}
          </span>{" "}
          <span className="text-app-body-2 text-neutral-675/50">
            {" "}
            {labels.interestPeriod}
          </span>
        </span>
      </Skeleton>
    </div>
  );
};

export default OfferBody;
